// @mentions DRAWN as the person's full name (user request 2026-10-15: "when tagging a user show
// the full name not the user name on the message"). Twin of the Swift/Kotlin/C# `MentionDisplay`
// helpers — same API, same tests.
//
// `@juan.perez` in a posted message reads `@Juan Pérez`. Only the drawing changes: the tap target
// stays `satori://user/juan.perez`, "Copiar texto" copies the raw message, and the composer / edit
// box never call this (the raw text is what gets sent) — substitution is opt-in through an explicit
// names map; an empty map is exactly the old rendering.
//
// The difficulty is offsets. Every Markdown span, `~channel` link and image placement was measured
// against the text BEFORE the rewrite, and a name rarely has the username's length. `rewrite` hands
// back the new text, the mentions at their new offsets, and `mapStart` / `mapEnd` to carry every
// other offset across. An offset strictly INSIDE a replaced mention snaps to the new mention's start
// (as a span start) or end (as a span end), so `**@juan**` and `[@juan](url)` still wrap the whole
// display name.
//
// The name is `TeammateNameDisplay.fullName` — NOT the name-display policy: the user asked for the
// full name. An empty full name keeps `@username`. Broadcast keywords never substitute, and neither
// does a mention inside a `code span` (it stays highlighted, as before, but code is verbatim text).
// Offsets are UTF-16 code units, the coordinate space `MentionHighlight` already uses.

import { specialMentions } from './mentionComposer';
import type { MentionSpan } from './mentionHighlight';

export interface MentionRewrite {
  /** The text as drawn. */
  text: string;
  /** Every input span (substituted or not), at its offsets in `text`. */
  mentions: MentionSpan[];
  /** An old offset used as a span START → its offset in `text`. */
  mapStart(offset: number): number;
  /** An old offset used as a span END → its offset in `text`. */
  mapEnd(offset: number): number;
}

interface Replacement {
  /** Old coordinates. */
  start: number;
  end: number;
  /** New coordinates. */
  newStart: number;
  newEnd: number;
}

const SPECIAL = new Set<string>(specialMentions);

const IDENTITY: Pick<MentionRewrite, 'mapStart' | 'mapEnd'> = {
  mapStart: (o) => o,
  mapEnd: (o) => o,
};

/**
 * Lookup key for `fullNamesByUsername`: the lowercased username. Mattermost usernames are
 * lowercase, but a mention typed `@Juan.Perez` must still find Juan.
 */
export function key(username: string): string {
  return username.toLowerCase();
}

/**
 * The display name for one mention, or null to keep `@username` (no entry, empty name, or a
 * broadcast keyword).
 */
function displayName(username: string, fullNamesByUsername: ReadonlyMap<string, string>): string | null {
  if (SPECIAL.has(username)) return null;
  const name = (fullNamesByUsername.get(key(username)) ?? '').trim();
  return name === '' ? null : name;
}

export function rewrite(
  text: string,
  mentionSpans: readonly MentionSpan[],
  fullNamesByUsername: ReadonlyMap<string, string>,
  /** Ranges whose text must stay verbatim (code spans): a mention overlapping one keeps `@username`. */
  verbatim: readonly { start: number; end: number }[] = [],
): MentionRewrite {
  if (fullNamesByUsername.size === 0 || mentionSpans.length === 0) {
    return { text, mentions: [...mentionSpans], ...IDENTITY };
  }
  const sorted = [...mentionSpans].sort((a, b) => a.start - b.start);
  const replacements: Replacement[] = [];
  const mentions: MentionSpan[] = [];
  let out = '';
  let cursor = 0;
  let delta = 0;
  for (const span of sorted) {
    // Defensive: an overlapping or out-of-range span keeps its text and is mapped like any offset.
    const valid = span.start >= cursor && span.start < span.end && span.end <= text.length;
    const inCode = verbatim.some((v) => v.start < span.end && span.start < v.end);
    const name = valid && !inCode ? displayName(span.username, fullNamesByUsername) : null;
    if (name === null) {
      if (valid) mentions.push({ ...span, start: span.start + delta, end: span.end + delta });
      continue;
    }
    out += text.slice(cursor, span.start);
    const display = '@' + name;
    const newStart = span.start + delta;
    const newEnd = newStart + display.length;
    out += display;
    replacements.push({ start: span.start, end: span.end, newStart, newEnd });
    mentions.push({ ...span, start: newStart, end: newEnd });
    delta = newEnd - span.end;
    cursor = span.end;
  }
  if (replacements.length === 0) return { text, mentions: [...mentionSpans], ...IDENTITY };
  out += text.slice(cursor);

  const map = (offset: number, inside: (r: Replacement) => number): number => {
    let shift = 0;
    for (const r of replacements) {
      if (offset <= r.start) break;
      if (offset >= r.end) {
        shift = r.newEnd - r.end;
        continue;
      }
      return inside(r);
    }
    return offset + shift;
  };
  return {
    text: out,
    mentions,
    mapStart: (o) => map(o, (r) => r.newStart),
    mapEnd: (o) => map(o, (r) => r.newEnd),
  };
}

export const MentionDisplay = {
  rewrite,
  key,
} as const;
